import { AppConfig } from '../config/AppConfig';
import { fetchWithRetry } from '../utils/fetchWithRetry';

export interface DemoClassInfo {
    classId: string;
    serverUrl: string;
    /**
     * WP-029: a freshly-minted single-use teacher code (10-min expiry) that the
     * child auto-submits via validate-teacher-code, so demo users pass through
     * the same teacher-code gate as a QR-scanned device. There is no path into
     * the system that skips validate-teacher-code.
     */
    teacherCode: string;
}

export class DemoService {
    /**
     * Fetches the fixed demo class info from the production sync-server.
     *
     * The demo is a known, fixed account (join code ABC-123) that lives on the
     * production sync-server, so this always targets `AppConfig.DEMO_SERVER_URL`
     * (production) — never the local-dev `DEFAULT_SERVER_URL`, which on a real
     * tablet resolves to the device itself and never has a server listening.
     *
     * Mirrors the stale-DNS resilience of `StrokeSyncService`: if the production
     * custom domain fails to resolve (common on Android due to negative DNS
     * caching), it falls back to the CNAME target / direct IP in
     * `DNS_FALLBACK_HOSTS` before giving up.
     */
    static async fetchDemoClassInfo(): Promise<DemoClassInfo> {
        const candidates = [AppConfig.DEMO_SERVER_URL, ...AppConfig.DNS_FALLBACK_HOSTS];
        let lastError: unknown = null;

        for (const baseUrl of candidates) {
            console.log(`[DemoService] Attempting demo class-info at ${baseUrl}/api/demo/class-info`);
            try {
                const res = await fetchWithRetry(`${baseUrl}/api/demo/class-info`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({}),
                    timeoutMs: 4000,
                    maxRetries: 0,
                });

                if (!res.ok) {
                    // Non-OK (e.g. 404 demo class not seeded) — try the next host;
                    // a different host may serve a DB where the demo class exists.
                    console.warn(`[DemoService] ${baseUrl} returned non-OK status ${res.status}`);
                    lastError = new Error('Let\'s try again!');
                    continue;
                }

                const data = await res.json();
                console.log(`[DemoService] Demo class-info resolved from ${baseUrl} — classId=${data.classId}, serverUrl=${data.serverUrl}`);
                return {
                    classId: data.classId,
                    serverUrl: data.serverUrl,
                    teacherCode: data.teacherCode,
                };
            } catch (err) {
                // Network error on this host — try the next fallback host.
                console.warn(`[DemoService] ${baseUrl} unreachable:`, (err as Error)?.message ?? err);
                lastError = err;
                continue;
            }
        }

        // All hosts exhausted — surface the gentle, child-friendly message.
        // Log the underlying failure so demo issues are diagnosable (the child
        // sees "Let's try again!" via HomeScreen's demoError state; never silent).
        console.warn('[DemoService] All demo server candidates failed:', lastError);
        throw new Error('Let\'s try again!');
    }
}
